// AI Context for managing AI settings, custom prompts, credits, and state
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isAIAvailable } from '../services/ai-config.js';
import { getCreditsBalance, formatCreditsDisplay, CREDIT_PACKAGES } from '../services/credits-service.js';
import { db } from '../services/firebase-config.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext.jsx';

const AIContext = createContext(null);

// AI Features that are enabled when AI is turned on
export const AI_FEATURES = [
    {
        id: 'summarization',
        name: 'Smart Summarization',
        icon: '✨',
        description: 'Generate concise bullet-point summaries of your notes'
    },
    {
        id: 'writing-assistant',
        name: 'AI Writing Assistant',
        icon: '✦',
        description: 'Restructure content, continue writing, and expand ideas'
    },
    {
        id: 'auto-tagging',
        name: 'Auto-Tagging',
        icon: '🏷️',
        description: 'Automatically categorize notes with smart tags'
    },
    {
        id: 'semantic-search',
        name: 'Semantic Search',
        icon: '🔍',
        description: 'Find notes by meaning, not just keywords'
    }
];

// Default prompts for AI features
export const DEFAULT_PROMPTS = {
    improve: `You are a professional content editor. Transform messy web content into clean, well-structured content.

TASK: Analyze content type, clean up junk, preserve links/images, and restructure like a professional article.

OUTPUT: Clean HTML with proper headings (h2/h3), paragraphs (p), lists (ul/ol), and emphasis (strong). Keep all <a> and <img> tags intact.`,

    expand: `You are a content developer. Expand the selected text with more details, examples, and explanations.

TASK: Add 3-5 more sentences elaborating on the topic. If it's a list, add more items. If it's an explanation, add depth and examples.

OUTPUT: Clean HTML matching the original format. Preserve all existing content including images and links.`,

    continue: `You are a writing assistant. Continue the document naturally from where it left off.

TASK: Write 2-4 sentences that flow naturally from the last paragraph, matching the style and tone.

OUTPUT: Clean HTML paragraphs that seamlessly continue the content.`
};

export function AIProvider({ children }) {
    const { user } = useAuth();
    const [aiEnabled, setAiEnabled] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const isUpdatingFirestore = useRef(false);

    const [aiConfigured, setAiConfigured] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);

    // Credits state
    const [credits, setCredits] = useState(null);
    const [creditsLoading, setCreditsLoading] = useState(false);

    // Custom prompts with localStorage persistence (prompts stay local for now)
    const [customPrompts, setCustomPrompts] = useState(() => {
        const saved = localStorage.getItem('page-notes-ai-prompts');
        if (saved) {
            try {
                return { ...DEFAULT_PROMPTS, ...JSON.parse(saved) };
            } catch {
                return DEFAULT_PROMPTS;
            }
        }
        return DEFAULT_PROMPTS;
    });

    // Check if AI is configured (API key present)
    useEffect(() => {
        setAiConfigured(isAIAvailable());
    }, []);

    // Load AI settings from Firestore when user logs in
    useEffect(() => {
        async function loadSettings() {
            if (!user) {
                // No user - use localStorage fallback
                const saved = localStorage.getItem('page-notes-ai-enabled');
                setAiEnabled(saved === 'true');
                setSettingsLoaded(true);
                return;
            }

            try {
                const settingsRef = doc(db, `users/${user.uid}/settings/preferences`);
                const settingsDoc = await getDoc(settingsRef);

                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    setAiEnabled(data.aiEnabled ?? false);
                } else {
                    // No settings yet - check localStorage for migration
                    const saved = localStorage.getItem('page-notes-ai-enabled');
                    const initialValue = saved === 'true';
                    setAiEnabled(initialValue);
                    // Save to Firestore
                    await setDoc(settingsRef, { aiEnabled: initialValue }, { merge: true });
                }
            } catch (error) {
                console.error('Error loading AI settings:', error);
                // Fallback to localStorage
                const saved = localStorage.getItem('page-notes-ai-enabled');
                setAiEnabled(saved === 'true');
            }
            setSettingsLoaded(true);
        }

        loadSettings();
    }, [user]);

    // Persist AI preference to Firestore when changed
    useEffect(() => {
        // Don't save until settings are loaded (prevents overwriting on initial load)
        if (!settingsLoaded) return;

        // Also save to localStorage as fallback
        localStorage.setItem('page-notes-ai-enabled', aiEnabled.toString());

        // Save to Firestore if user is logged in
        if (user && !isUpdatingFirestore.current) {
            const saveToFirestore = async () => {
                try {
                    const settingsRef = doc(db, `users/${user.uid}/settings/preferences`);
                    await setDoc(settingsRef, { aiEnabled }, { merge: true });
                } catch (error) {
                    console.error('Error saving AI settings:', error);
                }
            };
            saveToFirestore();
        }
    }, [aiEnabled, user, settingsLoaded]);

    // Persist custom prompts
    useEffect(() => {
        localStorage.setItem('page-notes-ai-prompts', JSON.stringify(customPrompts));
    }, [customPrompts]);

    // Fetch credits balance
    const refreshCredits = useCallback(async (forceRefresh = false) => {
        setCreditsLoading(true);
        try {
            const balance = await getCreditsBalance(forceRefresh);
            setCredits(balance);
        } catch (error) {
            console.error('Error fetching credits:', error);
        } finally {
            setCreditsLoading(false);
        }
    }, []);

    // Toggle AI features on/off
    const toggleAI = () => {
        if (!aiConfigured) {
            setAiError('AI not configured. Please add your Gemini API key to .env.local');
            return;
        }
        setAiError(null);
        setAiEnabled(prev => !prev);
    };

    // Check if a specific feature is active (AI enabled + configured)
    const isFeatureActive = (featureId) => {
        return aiEnabled && aiConfigured;
    };

    // Update a specific prompt
    const updatePrompt = (key, value) => {
        setCustomPrompts(prev => ({ ...prev, [key]: value }));
    };

    // Reset prompts to defaults
    const resetPrompts = () => {
        setCustomPrompts(DEFAULT_PROMPTS);
    };

    // Set loading state for AI operations
    const setLoading = (loading) => {
        setAiLoading(loading);
        if (loading) setAiError(null);
    };

    // Set error state
    const setError = (error) => {
        setAiError(error);
        setAiLoading(false);
    };

    // Clear error
    const clearError = () => {
        setAiError(null);
    };

    // Get formatted credits display
    const getCreditsDisplay = () => {
        if (!credits) return 'Loading...';
        return formatCreditsDisplay(credits);
    };

    const value = {
        aiEnabled,
        aiConfigured,
        aiLoading,
        aiError,
        customPrompts,
        credits,
        creditsLoading,
        toggleAI,
        isFeatureActive,
        updatePrompt,
        resetPrompts,
        setLoading,
        setError,
        clearError,
        refreshCredits,
        getCreditsDisplay,
        CREDIT_PACKAGES,
        AI_FEATURES,
        DEFAULT_PROMPTS
    };

    return (
        <AIContext.Provider value={value}>
            {children}
        </AIContext.Provider>
    );
}

export function useAI() {
    const context = useContext(AIContext);
    if (!context) {
        throw new Error('useAI must be used within an AIProvider');
    }
    return context;
}
